import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { resolveCoverUrl } from '../../services/api';
import styles from './StudyBooksPage.module.css';
import { AlertIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import FavoriteBooksView from './FavoriteBooksView';
import StudyHeader from './StudyHeader';

const StudyBooksPage = () => {
  const navigate = useNavigate();

  const [favoriteBooks, setFavoriteBooks] = useState([]);
  const [bookMetadata, setBookMetadata] = useState({});
  const [completedChapterCounts, setCompletedChapterCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const favoriteBookPreviews = useMemo(() => {
    if (!favoriteBooks.length) {
      return [];
    }
    const parseTime = (value) => {
      const timestamp = value ? Date.parse(value) : NaN;
      return Number.isFinite(timestamp) ? timestamp : 0;
    };
    return [...favoriteBooks]
      .sort((a, b) => parseTime(b.created_at) - parseTime(a.created_at))
      .slice(0, 3);
  }, [favoriteBooks]);

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        setError(null);

        const [booksResponse, completedResponse] = await Promise.all([
          api.get('/favorite-books/').catch(() => ({ data: [] })),
          api.get('/completed-chapters/').catch(() => ({ data: [] })),
        ]);

        const books = Array.isArray(booksResponse.data) ? booksResponse.data : [];
        setFavoriteBooks(books);

        const completedEntries = Array.isArray(completedResponse.data) ? completedResponse.data : [];
        const countsMap = completedEntries.reduce((acc, entry) => {
          if (entry?.novel_id == null) return acc;
          const key = String(entry.novel_id);
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        setCompletedChapterCounts(countsMap);

        const novelIds = Array.from(
          new Set(
            books
              .map((item) => item?.novel_id)
              .filter((id) => id !== null && id !== undefined)
              .map((id) => String(id))
          )
        );

        if (!novelIds.length) {
          setBookMetadata({});
          return;
        }

        const responses = await Promise.allSettled(
          novelIds.map(async (novelId) => {
            const response = await api.get(`/novels/${novelId}`);
            return { novelId, data: response.data };
          })
        );

        const meta = {};
        responses.forEach((result) => {
          if (result.status === 'fulfilled') {
            const key = String(result.value.novelId);
            const data = result.value.data || {};
            meta[key] = {
              ...data,
              cover_image_url: resolveCoverUrl(data.cover_image_url),
            };
          }
        });
        setBookMetadata(meta);
      } catch (err) {
        console.error('获取收藏书籍失败:', err);
        setError('获取收藏书籍失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, []);

  const handleDeleteBook = async (favoriteId) => {
    try {
      await api.delete(`/favorite-books/${favoriteId}`);
      setFavoriteBooks((prev) => prev.filter((item) => item.id !== favoriteId));
    } catch (err) {
      console.error('删除收藏书籍失败:', err);
      alert('取消收藏失败，请稍后重试');
    }
  };

  const handleOpenFavoriteBook = (favoriteBook) => {
    if (!favoriteBook?.novel_id) {
      return;
    }
    navigate(`/book?bookid=${favoriteBook.novel_id}`, { state: { from: '/study/books' } });
  };

  if (loading) {
    return (
      <div className={styles.study}>
        <LoadingSpinner fullPage text="加载收藏内容中..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.study}>
        <div className={styles.noHistory}>
          <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
          <h3>无法加载收藏内容</h3>
          <p>{error}</p>
          <button
            className={styles.retryBtn}
            onClick={() => window.location.reload()}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.study}>
      <div className={styles.detailView}>
        <StudyHeader title="收藏书籍" onBack={() => navigate('/study')} />
        <div className={styles.detailContent}>
          <FavoriteBooksView
            books={favoriteBooks}
            bookMetadata={bookMetadata}
            completedChapterCounts={completedChapterCounts}
            loading={false}
            error={null}
            onDeleteBook={handleDeleteBook}
            onOpenBookDetail={handleOpenFavoriteBook}
            favoriteBookPreviews={favoriteBookPreviews}
          />
        </div>
      </div>
    </div>
  );
};

export default StudyBooksPage;

